import { useCallback, useEffect, useRef, useState } from "react"
import backIcon from "../../assets/photo/back.svg"
import backLight from "../../assets/photo/back_dark.svg"
import { MyDispatch, MySelector } from "../../redux/store"
import { checkVideo } from "../../utils/isVideo"
import { Box, Modal } from "@mui/material"
import { useDispatch } from "react-redux"
import FullScreenNav from "../navs/FullScreenNav"
import { useLocation } from "react-router-dom"
import { setFullScreen, setFullScreenObject } from "../../redux/utilSlice"
import WatermarkOverlay from "../utils/WatermarkOverlay"
import { getOptimalUrl, getOptimalUrls } from "../../utils/getOptimalUrl"

interface Photos {
  id: string
  name: string
  url: string
  folderId: string
  compressedUrl?: string
  b2Url?: string
  b2CompressedUrl?: string
}

const style = {
  position: "fixed",
  height: "100vh",
  width: "100vw",
  outline: "none",
  display: "flex",
  flexDirection: "column",
}

const MediaContent = ({ item, onVideoEnd, eventData }: { item: Photos; onVideoEnd?: () => void; eventData?: any }) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [imageLoaded, setImageLoaded] = useState(false)
  const { url: optimalUrl, compressedUrl: optimalCompressedUrl } = getOptimalUrls(item)
  const hasCompressed = optimalCompressedUrl && optimalCompressedUrl !== optimalUrl

  // Reset image loaded state when item changes
  useEffect(() => {
    setImageLoaded(false)
  }, [item.id])

  if (checkVideo(optimalUrl)) {
    return (
      <video
        ref={videoRef}
        key={item.id}
        className="h-full object-contain"
        controls
        autoPlay
        playsInline
        muted
        preload="auto"
        onEnded={onVideoEnd}
      >
        <source src={optimalCompressedUrl} type="video/mp4" />
      </video>
    )
  }

  return (
    <div className="relative h-full">
      {/* Compressed image as placeholder */}
      {hasCompressed && !imageLoaded && (
        <img
          src={optimalCompressedUrl}
          alt={item.name}
          loading="eager"
          className="h-full object-contain"
        />
      )}

      {/* Original high-res image */}
      <img
        src={optimalUrl}
        alt={item.name}
        loading="eager"
        className={`h-full object-contain transition-opacity duration-300 ${
          hasCompressed ? (imageLoaded ? 'opacity-100' : 'opacity-0 absolute inset-0') : ''
        }`}
        onLoad={() => setImageLoaded(true)}
      />

      {/* Transparent shield so long-press / right-click can't save the original */}
      <div
        className="image-shield"
        onContextMenu={(e) => e.preventDefault()}
      />

      {/* Watermark Overlay */}
      {eventData?.watermarkEnabled && eventData?.watermarkText && (
        <WatermarkOverlay
          watermarkText={eventData.watermarkText}
          className="h-full"
        />
      )}
    </div>
  )
}

const NavigationButton = ({
  theme,
  direction,
  onClick,
}: {
  theme: string
  direction: "left" | "right"
  onClick: () => void
}) => (
  <img
    src={theme === "dark" ? backIcon : backLight}
    alt=""
    style={{ zIndex: 100 }}
    className={`p-1.5 h-[35px] w-[35px] cursor-pointer bg-white dark:bg-[#26272c]/80 rounded-full absolute top-[45%] ${
      direction === "left" ? "left-4" : "right-4 rotate-180"
    }`}
    onClick={onClick}
  />
)

const FullScreenModal = () => {
  const location = useLocation()
  const dispatch = useDispatch<MyDispatch>()

  const [slideItem, setSlideItem] = useState<Photos>()
  const [deleteRefObj, setDeleteRefObj] = useState<Photos | null>(null)
  const [slideFiles, setSlideFiles] = useState<Array<Photos>>()
  const [isSlideshow, setIsSlideshow] = useState(false)
  const slideshowInterval = useRef<NodeJS.Timeout | null>(null)

  const { theme } = MySelector((state) => state.theme)
  const { category, eventId, data: eventData } = MySelector((state) => state.event)
  const { activeAlbumFiles } = MySelector((state) => state.gallery)
  const { fullScreenObject, type, currentFolderData, fullScreen, deleteArray } =
    MySelector((state) => state.util)

  // Special event ID that should use cover photo as background
  const specialEventId = "9f5e3f10-eeef-48eb-afce-620cbf98698f"
  const shouldUseCoverBackground = eventId === specialEventId
  const coverPhotoUrl = eventData?.coverPhotoLink

  const currentIndex =
    slideFiles?.findIndex((sf) => sf.id === slideItem?.id) ?? -1
  const hasLeft = currentIndex > 0
  const hasRight = slideFiles ? currentIndex < slideFiles.length - 1 : false

  // navigate slide dynamically
  const navigateSlide = useCallback(
    (direction: "left" | "right") => {
      if (!slideFiles || !slideItem) return

      const newIndex =
        direction === "left" ? currentIndex - 1 : currentIndex + 1
      if (newIndex < 0 || newIndex >= slideFiles.length) return

      const newSlide = slideFiles[newIndex]
      setSlideItem(newSlide)
      dispatch(setFullScreenObject(newSlide))
    },
    [slideFiles, slideItem, currentIndex, dispatch]
  )

  // slideshow controls
  const toggleSlideshow = useCallback(() => {
    setIsSlideshow(prev => !prev)
  }, [])

  // advance to next slide
  const advanceSlide = useCallback(() => {
    setSlideFiles(currentSlideFiles => {
      if (!currentSlideFiles) return currentSlideFiles

      setSlideItem(currentSlideItem => {
        if (!currentSlideItem) return currentSlideItem

        const currentIndex = currentSlideFiles.findIndex((sf) => sf.id === currentSlideItem.id)

        // Move to next file (including videos)
        const newIndex = currentIndex + 1

        if (newIndex >= currentSlideFiles.length) {
          // Stop slideshow when reaching the end
          setIsSlideshow(false)
          return currentSlideItem
        }

        const newSlide = currentSlideFiles[newIndex]
        dispatch(setFullScreenObject(newSlide))
        return newSlide
      })

      return currentSlideFiles
    })
  }, [dispatch])

  // handle video end during slideshow
  const handleVideoEnd = useCallback(() => {
    if (isSlideshow) {
      advanceSlide()
    }
  }, [isSlideshow, advanceSlide])

  // manage slideshow interval
  useEffect(() => {
    if (isSlideshow && slideItem) {
      // Clear any existing interval
      if (slideshowInterval.current) {
        clearInterval(slideshowInterval.current)
        slideshowInterval.current = null
      }

      // If current item is not a video, set timer for images
      if (!checkVideo(getOptimalUrl(slideItem, false))) {
        slideshowInterval.current = setInterval(advanceSlide, 3000)
      }
      // For videos, we rely on the onEnded event to advance
    } else {
      if (slideshowInterval.current) {
        clearInterval(slideshowInterval.current)
        slideshowInterval.current = null
      }
    }

    return () => {
      if (slideshowInterval.current) {
        clearInterval(slideshowInterval.current)
        slideshowInterval.current = null
      }
    }
  }, [isSlideshow, slideItem, advanceSlide])


  // handle close
  const handleClose = useCallback(() => {
    dispatch(setFullScreen(false))
    dispatch(setFullScreenObject(null))
  }, [dispatch])

  // to handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft" && hasLeft) {
        navigateSlide("left")
      } else if (event.key === "ArrowRight" && hasRight) {
        navigateSlide("right")
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [navigateSlide, hasLeft, hasRight])

  // to set current slide, and all slides
  useEffect(() => {
    if (!fullScreenObject || !currentFolderData) return
    if (type === "physical" && !activeAlbumFiles) return

    setSlideItem(fullScreenObject)
    setSlideFiles(type === "gallery" ? currentFolderData : activeAlbumFiles || [])
  }, [fullScreenObject, currentFolderData, activeAlbumFiles, type, category])

  // to update deleteRefObj whenever slideItem changes
  useEffect(() => {
    if (!slideFiles?.length || !slideItem) return

    const currentIndex = slideFiles.findIndex((sf) => sf.id === slideItem.id)
    if (currentIndex === -1) return

    setDeleteRefObj(
      slideFiles[currentIndex - 1] || slideFiles[currentIndex + 1] || null
    )
  }, [slideItem, slideFiles])

  // to check if current file is deleted or not and update the fullscreenobject if deleted
  useEffect(() => {
    if (!slideItem || !slideFiles?.length) return

    const exists = slideFiles.some((sf) => sf.id === slideItem.id)
    if (!exists && deleteRefObj) {
      setSlideItem(deleteRefObj)
      dispatch(setFullScreenObject(deleteRefObj))
    }
  }, [deleteArray, slideItem, slideFiles, deleteRefObj, dispatch])

  // handle clicking back on the modal
  useEffect(() => {
    if (fullScreen) {
      window.history.pushState(null, "", window.location.href)
    }

    const handleBack = (event: PopStateEvent) => {
      if (fullScreen) {
        event.preventDefault()
        handleClose()
        window.history.pushState(null, "", window.location.href)
      }
    }

    window.addEventListener("popstate", handleBack)
    return () => {
      window.removeEventListener("popstate", handleBack)
    }
  }, [fullScreen, handleClose])

  if (!slideItem || location.pathname.split("/")[2] === "file") return <></>

  return (
    <Modal
      open={fullScreen}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          ...style,
          background: shouldUseCoverBackground && coverPhotoUrl
            ? `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url("${coverPhotoUrl}")`
            : theme === "dark" ? "#000000" : "#ffffff",
          backgroundSize: shouldUseCoverBackground && coverPhotoUrl ? 'cover' : 'auto',
          backgroundPosition: shouldUseCoverBackground && coverPhotoUrl ? 'center' : 'initial',
          backgroundRepeat: shouldUseCoverBackground && coverPhotoUrl ? 'no-repeat' : 'repeat',
        }}
      >
        <FullScreenNav isSlideshow={isSlideshow} onToggleSlideshow={toggleSlideshow} />

        {hasLeft && (
          <NavigationButton
            theme={theme}
            direction="left"
            onClick={() => navigateSlide("left")}
          />
        )}


        <div className={`flex flex-1 justify-center items-center overflow-hidden min-h-0 max-h-[calc(100vh-80px)] md:-translate-y-0 ${
          shouldUseCoverBackground ? 'bg-transparent' : 'bg-white dark:bg-black'
        }`}>
          <MediaContent item={slideItem} onVideoEnd={handleVideoEnd} eventData={eventData} />
        </div>

        {/* Fixed height block for mobile browser UI */}
        <div className="h-16 md:h-0 shrink-0" />

        {hasRight && (
          <NavigationButton
            theme={theme}
            direction="right"
            onClick={() => navigateSlide("right")}
          />
        )}
      </Box>
    </Modal>
  )
}

export default FullScreenModal
